/**
 * CaribbeanLeaf Customer Portal Route Configuration
 *
 * Lazy-loaded routes for B2C customer-facing pages.
 * Separate from staff routes with independent auth protection.
 */

import { lazy, Suspense, ReactNode } from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useCustomerAuth } from '@/hooks/useCustomerAuth';
import { CustomerLayout } from '@/layouts/CustomerLayout';
import { Skeleton } from '@/components/ui/skeleton';
import { CUSTOMER_BASE, customerPath } from '@/config/customer';

// ─────────────────────────────────────────────────────────────────────────────
// Lazy-loaded pages for code splitting
// ─────────────────────────────────────────────────────────────────────────────

const LandingPage = lazy(() => import('@/pages/customer/LandingPage'));
const StoreLocatorPage = lazy(
  () => import('@/pages/customer/StoreLocatorPage')
);
const LoginPage = lazy(() => import('@/pages/customer/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/customer/RegisterPage'));
const VerifyMedicalPage = lazy(
  () => import('@/pages/customer/VerifyMedicalPage')
);
const PendingVerificationPage = lazy(
  () => import('@/pages/customer/PendingVerificationPage')
);
const ForgotPasswordPage = lazy(
  () => import('@/pages/customer/ForgotPasswordPage')
);
const ResetPasswordPage = lazy(
  () => import('@/pages/customer/ResetPasswordPage')
);

// Real pages (replacing placeholders)
const CatalogPage = lazy(() => import('@/pages/customer/CatalogPage'));
const ProductDetailPage = lazy(
  () => import('@/pages/customer/ProductDetailPage')
);
const CartPage = lazy(() => import('@/pages/customer/CartPage'));
const OrderHistoryPage = lazy(
  () => import('@/pages/customer/OrderHistoryPage')
);
const OrderDetailPage = lazy(
  () => import('@/pages/customer/OrderDetailPage')
);
const AccountDashboardPage = lazy(
  () => import('@/pages/customer/AccountDashboardPage')
);
const AccountSettingsPage = lazy(
  () => import('@/pages/customer/AccountSettingsPage')
);
const SavedAddressesPage = lazy(
  () => import('@/pages/customer/SavedAddressesPage')
);
const VerifyTouristPermitPage = lazy(
  () => import('@/pages/customer/VerifyTouristPermitPage')
);

// Checkout flow
const CheckoutFulfillmentPage = lazy(
  () => import('@/pages/customer/checkout/CheckoutFulfillmentPage')
);
const CheckoutPaymentPage = lazy(
  () => import('@/pages/customer/checkout/CheckoutPaymentPage')
);
const OrderConfirmationPage = lazy(
  () => import('@/pages/customer/checkout/OrderConfirmationPage')
);

// Sacramental
const SacramentalInfoPage = lazy(
  () => import('@/pages/customer/SacramentalInfoPage')
);
const SacramentalApplicationPage = lazy(
  () => import('@/pages/customer/SacramentalApplicationPage')
);
const SacramentalStatusPage = lazy(
  () => import('@/pages/customer/SacramentalStatusPage')
);

const AboutPage = lazy(() => import('@/pages/customer/AboutPage'));

// ─────────────────────────────────────────────────────────────────────────────
// Loading fallback
// ─────────────────────────────────────────────────────────────────────────────

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="space-y-4 w-full max-w-md p-8">
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-32 w-full" />
      </div>
    </div>
  );
}

function LazyPage({ children }: { children: ReactNode }) {
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Route Wrappers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Layout wrapper - applies CustomerLayout to all customer routes
 */
function CustomerLayoutWrapper() {
  return (
    <CustomerLayout>
      <Outlet />
    </CustomerLayout>
  );
}

/**
 * Protected route - requires customer authentication
 */
function CustomerProtectedRoute() {
  const { isAuthenticated, isLoading } = useCustomerAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (!isAuthenticated) {
    return <Navigate to={customerPath('/login')} replace />;
  }

  return <Outlet />;
}

/**
 * Guest route - only accessible when NOT authenticated
 * Redirects authenticated customers to their account
 */
function CustomerGuestRoute() {
  const { isAuthenticated, isLoading } = useCustomerAuth();

  if (isLoading) {
    return <PageLoader />;
  }

  if (isAuthenticated) {
    return <Navigate to={customerPath('/account')} replace />;
  }

  return <Outlet />;
}

// ─────────────────────────────────────────────────────────────────────────────
// Route Definitions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Customer Portal Routes
 *
 * Structure:
 * - / (public - landing page)
 * - /locations (public - store locator)
 * - /shop (public - product catalog)
 * - /shop/:id (public - product detail)
 * - /sacramental (public - sacramental info)
 * - /about (public - about us)
 * - /login (guest only)
 * - /register (guest only)
 * - /verify-medical (guest only - after registration)
 * - /pending-verification (guest only - waiting for approval)
 * - /cart (protected - shopping cart)
 * - /account (protected - account dashboard)
 * - /orders (protected - order history)
 * - /orders/:id (protected - order detail)
 * - /checkout/fulfillment (protected - checkout step 1)
 * - /checkout/payment (protected - checkout step 2)
 * - /checkout/confirmation (protected - order confirmation)
 * - /sacramental/apply (protected - sacramental application)
 * - /sacramental/status (protected - sacramental status)
 */
export const customerRoutes = [
  {
    element: <CustomerLayoutWrapper />,
    children: [
      // ─────────────────────────────────────────────────────────────────
      // Public routes (accessible to everyone)
      // ─────────────────────────────────────────────────────────────────
      {
        path: CUSTOMER_BASE,
        element: (
          <LazyPage>
            <LandingPage />
          </LazyPage>
        ),
      },
      {
        path: customerPath('/locations'),
        element: (
          <LazyPage>
            <StoreLocatorPage />
          </LazyPage>
        ),
      },
      {
        path: customerPath('/shop'),
        element: (
          <LazyPage>
            <CatalogPage />
          </LazyPage>
        ),
      },
      {
        path: customerPath('/shop/:id'),
        element: (
          <LazyPage>
            <ProductDetailPage />
          </LazyPage>
        ),
      },
      {
        path: customerPath('/sacramental'),
        element: (
          <LazyPage>
            <SacramentalInfoPage />
          </LazyPage>
        ),
      },
      {
        path: customerPath('/about'),
        element: (
          <LazyPage>
            <AboutPage />
          </LazyPage>
        ),
      },

      // ─────────────────────────────────────────────────────────────────
      // Guest-only routes (redirect to account if authenticated)
      // ─────────────────────────────────────────────────────────────────
      {
        element: <CustomerGuestRoute />,
        children: [
          {
            path: customerPath('/login'),
            element: (
              <LazyPage>
                <LoginPage />
              </LazyPage>
            ),
          },
          {
            path: customerPath('/register'),
            element: (
              <LazyPage>
                <RegisterPage />
              </LazyPage>
            ),
          },
          {
            path: customerPath('/verify-medical'),
            element: (
              <LazyPage>
                <VerifyMedicalPage />
              </LazyPage>
            ),
          },
          {
            path: customerPath('/pending-verification'),
            element: (
              <LazyPage>
                <PendingVerificationPage />
              </LazyPage>
            ),
          },
          {
            path: customerPath('/forgot-password'),
            element: (
              <LazyPage>
                <ForgotPasswordPage />
              </LazyPage>
            ),
          },
          {
            path: customerPath('/reset-password'),
            element: (
              <LazyPage>
                <ResetPasswordPage />
              </LazyPage>
            ),
          },
        ],
      },

      // ─────────────────────────────────────────────────────────────────
      // Protected routes (require authentication)
      // ─────────────────────────────────────────────────────────────────
      {
        element: <CustomerProtectedRoute />,
        children: [
          {
            path: customerPath('/cart'),
            element: (
              <LazyPage>
                <CartPage />
              </LazyPage>
            ),
          },
          {
            path: customerPath('/account'),
            element: (
              <LazyPage>
                <AccountDashboardPage />
              </LazyPage>
            ),
          },
          {
            path: customerPath('/account/settings'),
            element: (
              <LazyPage>
                <AccountSettingsPage />
              </LazyPage>
            ),
          },
          {
            path: customerPath('/account/addresses'),
            element: (
              <LazyPage>
                <SavedAddressesPage />
              </LazyPage>
            ),
          },
          {
            path: customerPath('/orders'),
            element: (
              <LazyPage>
                <OrderHistoryPage />
              </LazyPage>
            ),
          },
          {
            path: customerPath('/orders/:id'),
            element: (
              <LazyPage>
                <OrderDetailPage />
              </LazyPage>
            ),
          },
          {
            path: customerPath('/checkout/fulfillment'),
            element: (
              <LazyPage>
                <CheckoutFulfillmentPage />
              </LazyPage>
            ),
          },
          {
            path: customerPath('/checkout/payment'),
            element: (
              <LazyPage>
                <CheckoutPaymentPage />
              </LazyPage>
            ),
          },
          {
            path: customerPath('/checkout/confirmation'),
            element: (
              <LazyPage>
                <OrderConfirmationPage />
              </LazyPage>
            ),
          },
          {
            path: customerPath('/sacramental/apply'),
            element: (
              <LazyPage>
                <SacramentalApplicationPage />
              </LazyPage>
            ),
          },
          {
            path: customerPath('/sacramental/status'),
            element: (
              <LazyPage>
                <SacramentalStatusPage />
              </LazyPage>
            ),
          },
          {
            path: customerPath('/verify/tourist'),
            element: (
              <LazyPage>
                <VerifyTouristPermitPage />
              </LazyPage>
            ),
          },
        ],
      },
    ],
  },
];

export default customerRoutes;
